import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IMyOptions } from 'ng-uikit-pro-standard';
import { TranslateService } from '@ngx-translate/core';
import { timer } from 'rxjs';
import { ICustomer } from '../../../../models/customer.model';
import { AlertService } from '../../../../services/alert.service';
import { StatsButton } from '../../../../models/stats-button';
import { StatsPeriod } from '../../../../models/stats-period.model';
import { ChartsStatsService } from './charts-stats.service';
import { CurrentDataService } from '../../../../services/current-data-service';

@Component({
  selector: 'jhi-charts-stats',
  templateUrl: './charts-stats-component.html',
  styleUrls: ['./charts-stats-component.scss'],
})
export class ChartsStatsComponent implements OnInit {
  @Input() public titleKey: string | undefined;
  @Input() public billDate: boolean = false;
  @Input() public buttons!: StatsButton[];
  @Input() public id!: number;
  @Input() public table!: string;
  @Input() public maskOptions: boolean = false;
  period: StatsPeriod = StatsPeriod.CurrentYear;
  startDate: Date | undefined;
  endDate: Date | undefined;
  currentData!: StatsButton;
  loading = true;
  optionsSelect!: Array<any>;
  personalize = false;
  myDatePickerOptions!: IMyOptions;
  customer!: ICustomer;
  controllerDatePicker!: string;

  constructor(
    public activatedRoute: ActivatedRoute,
    public translate: TranslateService,
    public dataService: ChartsStatsService,
    public currentDataService: CurrentDataService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.currentDataService.getCustomer().subscribe(value => {
      if (value) {
        this.customer = value;
      }
    });
    this.currentData = this.buttons[0];
    this.initializeOptions();
    this.dataService.refreshButtons(this.customer, this.buttons, this.table, this.period, this.id, this.startDate, this.endDate, this.billDate);
    this.refreshGraph();
  }

  changeCurrentData(dataType: StatsButton): void {
    this.currentData = dataType;
    this.refreshGraph();
  }

  public refreshGraph(): void {
    this.setTimer();
    this.alertService.clear();
    this.controllerDatePicker = '';
    if (this.period === StatsPeriod.Perso && this.startDate && this.endDate) {
      this.dataService.getPersoStats(this.customer, this.currentData.query, this.table, this.startDate, this.endDate, this.id, this.billDate, this.maskOptions);
    } else {
      this.dataService.getStats(this.customer, this.currentData.query, this.table, this.period, this.id, this.billDate, this.maskOptions);
    }
  }

  public setTimer(): void {
    this.loading = true;
    const source = timer(750);
    source.subscribe(() => {
      this.loading = false;
    });
  }

  public changePeriod(): void {
    if (this.period === StatsPeriod.Perso) {
      this.personalize = true;
    } else {
      this.personalize = false;
      this.dataService.refreshButtons(this.customer, this.buttons, this.table, this.period, this.id, this.startDate, this.endDate, this.billDate);
      this.refreshGraph();
    }
  }

  public onDateChange(event: any, startOrEnd: 'start' | 'end'): void {
    if (event.epoc) {
      const newDate = new Date(event.date.year, event.date.month - 1, event.date.day);

      if (startOrEnd === 'start') {
        this.startDate = newDate;
      } else {
        this.endDate = newDate;
      }

      if ((startOrEnd === 'start' && this.endDate) || (startOrEnd === 'end' && this.startDate)) {
        this.controllerDatePicker = '';
        if (this.startDate! > this.endDate!) {
          this.alertService.error('La date de début doit être inférieure à la date de fin.');
          this.controllerDatePicker = 'La date de début doit être inférieure à la date de fin.';
        } else {
          this.dataService.refreshButtons(this.customer, this.buttons, this.table, this.period, this.id, this.startDate, this.endDate, this.billDate);
          this.refreshGraph();
        }
      }
    }
  }

  private initializeOptions(): void {
    this.optionsSelect = [
      { value: StatsPeriod.CurrentYear, label: 'Année en cours' },
      { value: StatsPeriod.CurrentMonth, label: 'Mois en cours' },
      { value: StatsPeriod.CurrentWeek, label: 'Semaine en cours' },
      { value: StatsPeriod.AllTime, label: 'Tout temps' },
      { value: StatsPeriod.Perso, label: 'Personnaliser...' },
    ];
    this.myDatePickerOptions = {
      dayLabels: {
        su: this.translate.instant('baryApp.customer-statistics.charts.datepicker.dayofweek.sunday'),
        mo: this.translate.instant('baryApp.customer-statistics.charts.datepicker.dayofweek.monday'),
        tu: this.translate.instant('baryApp.customer-statistics.charts.datepicker.dayofweek.tuesday'),
        we: this.translate.instant('baryApp.customer-statistics.charts.datepicker.dayofweek.wednesday'),
        th: this.translate.instant('baryApp.customer-statistics.charts.datepicker.dayofweek.thursday'),
        fr: this.translate.instant('baryApp.customer-statistics.charts.datepicker.dayofweek.friday'),
        sa: this.translate.instant('baryApp.customer-statistics.charts.datepicker.dayofweek.saturday'),
      },
      monthLabels: {
        1: this.translate.instant('baryApp.customer-statistics.charts.datepicker.monthsortcut.january'),
        2: this.translate.instant('baryApp.customer-statistics.charts.datepicker.monthsortcut.february'),
        3: this.translate.instant('baryApp.customer-statistics.charts.datepicker.monthsortcut.march'),
        4: this.translate.instant('baryApp.customer-statistics.charts.datepicker.monthsortcut.april'),
        5: this.translate.instant('baryApp.customer-statistics.charts.datepicker.monthsortcut.may'),
        6: this.translate.instant('baryApp.customer-statistics.charts.datepicker.monthsortcut.june'),
        7: this.translate.instant('baryApp.customer-statistics.charts.datepicker.monthsortcut.july'),
        8: this.translate.instant('baryApp.customer-statistics.charts.datepicker.monthsortcut.august'),
        9: this.translate.instant('baryApp.customer-statistics.charts.datepicker.monthsortcut.september'),
        10: this.translate.instant('baryApp.customer-statistics.charts.datepicker.monthsortcut.october'),
        11: this.translate.instant('baryApp.customer-statistics.charts.datepicker.monthsortcut.november'),
        12: this.translate.instant('baryApp.customer-statistics.charts.datepicker.monthsortcut.december'),
      },
      monthLabelsFull: {
        1: this.translate.instant('baryApp.customer-statistics.charts.datepicker.month.january'),
        2: this.translate.instant('baryApp.customer-statistics.charts.datepicker.month.february'),
        3: this.translate.instant('baryApp.customer-statistics.charts.datepicker.month.march'),
        4: this.translate.instant('baryApp.customer-statistics.charts.datepicker.month.april'),
        5: this.translate.instant('baryApp.customer-statistics.charts.datepicker.month.may'),
        6: this.translate.instant('baryApp.customer-statistics.charts.datepicker.month.june'),
        7: this.translate.instant('baryApp.customer-statistics.charts.datepicker.month.july'),
        8: this.translate.instant('baryApp.customer-statistics.charts.datepicker.month.august'),
        9: this.translate.instant('baryApp.customer-statistics.charts.datepicker.month.september'),
        10: this.translate.instant('baryApp.customer-statistics.charts.datepicker.month.october'),
        11: this.translate.instant('baryApp.customer-statistics.charts.datepicker.month.november'),
        12: this.translate.instant('baryApp.customer-statistics.charts.datepicker.month.december'),
      },
      // Buttons
      todayBtnTxt: this.translate.instant('baryApp.customer-statistics.charts.datepicker.today'),
      clearBtnTxt: this.translate.instant('baryApp.customer-statistics.charts.datepicker.clean'),
      closeBtnTxt: this.translate.instant('baryApp.customer-statistics.charts.datepicker.close'),
      // Format
      dateFormat: 'dd.mm.yyyy',
      firstDayOfWeek: 'mo',
    };
  }
}
